import React, { useEffect, useState } from 'react';
import ApplicationPageTemplate from '../../../dashboard/components/ApplicationPageTemplate/ApplicationPageTemplate';
import ProductContainer from './components/ProductContainer';
import { DragDropContext } from 'react-beautiful-dnd';
import styled from 'styled-components';
import { CountryList } from 'src/components/CountryList';
import { Button } from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import SortIcon from '@mui/icons-material/Sort';
import { useDispatch, useSelector } from 'react-redux';
import CircularProgress from '@mui/material/CircularProgress';

import { fetchCategory, selectCategoryByCountry } from '@store/store-category-management';
import { getProducts, updateSort } from '@store/sort-sku-management';
import { showErrorMessage, showSuccessMessage } from '@store/dashboard';
import { ConfirmationDialog } from 'src/components/ConfirmationDialog';
import { breadcrumbsActions } from '@store/breadcrumbs';
import Dropdown from 'src/components/Dropdown';

const columns = [
	{
		id: 'unsorted',
		title: 'All Products',
		products: [],
	},
	{
		id: 'sorted',
		title: 'Sorted Products',
		products: [],
	},
];

const OptionContainer = styled.div`
	padding: 16px;
	display: flex;
	justify-content: space-between;
	align-items: flex-end;
`;

const FilterContainer = styled.div`
	display: flex;
	flex-direction: row;
	align-items: center;
`;

const FilterItem = styled.div`
	margin-right: 10px;
	width: 200px;
`;

const SortingOptionContainer = styled.div``;

const DraggableContainer = styled.div`
	display: flex;
	flex-direction: row;
	flex-wrap: nowrap;
	padding: 0 16px;
	justify-content: space-between;
`;

const ActionContainer = styled.div`
	padding: 18px;
	display: flex;
	justify-content: flex-end;
`;

const ActionButton = styled(Button)`
	margin-left: 8px;
`;

const LoaderContainer = styled.div`
	display: flex;
	margin: 16px;
	justify-content: center;
	border-radius: 4px;
	background-color: white;
	padding: 32px 0;
	border: 1px solid lightgrey;
`;

const FilterLabel = styled.div`
	font-family: 'Roboto', 'Helvetica', 'Arial', sans-serif;
	font-size: 0.9rem;
	font-weight: 500;
	margin: 0 0 5px 0;
`;

const SortSku = () => {
	const dispatch = useDispatch();

	const { status: categoryStatus } = useSelector((state) => state.giftCategoryList);
	const { status: countryStatus } = useSelector((state) => state.giftCountryList);
	const products = useSelector((state) => state.giftSortSku.products);

	const [productList, setProductList] = useState(columns);
	const [selectedCountry, setSelectedCountry] = useState('my');
	const [selectedCategory, setSelectedCategory] = useState(null);
	const [isLoading, setIsLoading] = useState(false);
	const [unsortedProduct, setUnsortedProduct] = useState([]);

	const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
	const [confirmationDialogData, setConfirmationDialogData] = useState({});

	const categoryByCountry = useSelector((state) =>
		selectCategoryByCountry(state, selectedCountry)
	);

	useEffect(() => {
		if (!!selectedCountry) {
			dispatch(fetchCategory());
		}
	}, [selectedCountry, dispatch]);

	useEffect(() => {
		if (!!categoryByCountry && !selectedCategory) {
			setSelectedCategory(categoryByCountry[0].id);
		}
	}, [categoryByCountry]); // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		if (!!selectedCategory && !!selectedCountry) {
			dispatch(getProducts({ countryCode: selectedCountry, categoryId: selectedCategory }));
		}
	}, [selectedCategory, selectedCountry, dispatch]);

	useEffect(() => {
		if (products && products.length > 0) {
			const productListState = [...productList];
			productListState[0].products = [...products].filter(
				(product) => product.sortingOrder === null || product.sortingOrder < 0
			);
			productListState[1].products = [...products].filter(
				(product) => product.sortingOrder !== null && product.sortingOrder >= 0
			);
			productListState[1].products.sort((a, b) => a.sortingOrder - b.sortingOrder);
			setProductList([...productListState]);
		} else {
			setProductList((prevState) => [
				...prevState.map((prev) => ({ ...prev, products: [] })),
			]);
		}
	}, [products]); // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		dispatch(
			breadcrumbsActions.setBreadcrumbs([
				{ label: 'Home', url: '/' },
				{ label: 'Gift Sort SKU', url: '/gift-sku-management/sort' },
			])
		);
	}, [dispatch]);

	const dragEndHandler = (result) => {
		const { destination, source } = result;

		const sourceIndex = source.droppableId;
		const destinationIndex = destination.droppableId;

		if (sourceIndex === destinationIndex) {
			const column = productList[sourceIndex];
			const newProducts = [...column.products];

			const removedProduct = newProducts[source.index];
			newProducts.splice(source.index, 1);
			newProducts.splice(destination.index, 0, removedProduct);

			const newState = [...productList];
			newState[sourceIndex] = {
				...column,
				products: [...newProducts],
			};

			setProductList(newState);
		} else {
			const dColumn = productList[destinationIndex];
			const dNewProducts = [...dColumn.products];

			if (dColumn.id === 'sorted' && dNewProducts.length >= 10) {
				dispatch(showErrorMessage(`Only 10 products can be sorted`));
				return;
			}

			const sColumn = productList[sourceIndex];
			const sNewProducts = [...sColumn.products];

			const removedProduct = sNewProducts[source.index];
			sNewProducts.splice(source.index, 1);

			const sNewColumn = {
				...sColumn,
				products: [...sNewProducts],
			};

			dNewProducts.splice(destination.index, 0, removedProduct);

			const dNewColumn = {
				...dColumn,
				products: [...dNewProducts],
			};

			if (
				sColumn.id === 'sorted' &&
				dColumn.id === 'unsorted' &&
				removedProduct.sortingOrder >= 0 &&
				removedProduct.sortingOrder !== null
			) {
				setUnsortedProduct((prevState) => [...prevState, removedProduct]);
			}

			if (
				sColumn.id === 'unsorted' &&
				dColumn.id === 'sorted' &&
				removedProduct.sortingOrder >= 0 &&
				removedProduct.sortingOrder !== null
			) {
				const filteredUnsorted = unsortedProduct.filter(
					(product) => product.id !== removedProduct.id
				);
				setUnsortedProduct(filteredUnsorted);
			}

			const newState = [...productList];
			newState[source.droppableId] = { ...sNewColumn };
			newState[destinationIndex] = { ...dNewColumn };

			setProductList(newState);
		}
	};

	const handlSelectChange = (country) => {
		if (!country) return;
		setSelectedCategory(null);
		setSelectedCountry(country);
	};

	const categoryChangeHandler = (e) => {
		if (!e ) return;
		setSelectedCategory(e.id);
	};

	const countryListProps = {
		required: true,
		fullWidth: true,
		width: '250px',
		margin: 'normal',
	};

	const submitSortHandler = () => {
		setIsLoading(true);
		const sortedProducts = productList[1];

		const mappedProducts = [...sortedProducts.products].map((product, index) => ({
			id: product.id,
			sortingOrder: +index,
		}));

		const mappedUnsortedProducts = [...unsortedProduct].map((product) => ({
			id: product.id,
			sortingOrder: -99,
		}));

		const payload = { products: [...mappedProducts, ...mappedUnsortedProducts] };

		dispatch(
			updateSort({ countryCode: selectedCountry, categoryId: selectedCategory, payload })
		)
			.then(() => {
				setUnsortedProduct([]);
				setIsLoading(false);
				dispatch(
					getProducts({ countryCode: selectedCountry, categoryId: selectedCategory })
				);
				dispatch(showSuccessMessage('Successfully sorted the products!'));
			})
			.catch((error) => {
				setUnsortedProduct([]);
				setIsLoading(false);
				dispatch(showErrorMessage(`Something went wrong while sorting`));
			});
	};

	const resetProducts = () => {
		dispatch(getProducts({ countryCode: selectedCountry, categoryId: selectedCategory }));
	};

	const handleShowConfirmationDialog = () => {
		setConfirmationDialogData({
			title: 'You are about to sort by top selling',
			message: 'Are you sure you want to sort the products by top selling?',
		});

		setShowConfirmationDialog(true);
	};

	const closeConfirmationDialog = () => {
		setConfirmationDialogData({});

		setShowConfirmationDialog(false);
	};

	const sortProductsByTrending = () => {
		if (!products || products.length <= 0) {
			setConfirmationDialogData({});
			setShowConfirmationDialog(false);
			return;
		}

		const purchasedProducts = [...products].filter(
			(product) => product.purchaseCount !== null && product.purchaseCount > 0
		);
		const unpurchasedProducts = [...products].filter(
			(product) =>
				product.purchaseCount === null ||
				product.purchaseCount === undefined ||
				product.purchaseCount === 0
		);

		const sortedPurchasedProduct = [...purchasedProducts].sort(
			(a, b) => b.purchaseCount - a.purchaseCount
		);

		const oldSortedProducts = [...products].filter(
			(product) => product.sortingOrder !== null && product.sortingOrder >= 0
		);

		const productsToUnsort = oldSortedProducts.filter(
			(product) => !sortedPurchasedProduct.some((sort) => sort.id === product.id)
		);

		const productListState = [...columns];
		productListState[0].products = [...unpurchasedProducts];
		productListState[1].products = [...sortedPurchasedProduct];
		setProductList([...productListState]);
		setUnsortedProduct([...productsToUnsort]);

		setConfirmationDialogData({});
		setShowConfirmationDialog(false);
	};

	return (
		<ApplicationPageTemplate
			title={'Sort SKU Management'}
			description={'Sort products'}
			pageReady={true}
		>
			<OptionContainer>
				<FilterContainer>
					<FilterItem>
						<FilterLabel>Country</FilterLabel>
						<CountryList
							{...countryListProps}
							onChange={handlSelectChange}
							placeholder="Select Country"
							id="country"
							value={selectedCountry}
							disabled={countryStatus === 'loading'}
							width="100%"
							background="white"
						/>
					</FilterItem>
					<FilterItem>
						<FilterLabel>Category</FilterLabel>
						<Dropdown
							options={[...categoryByCountry].map((category) => ({
								id: category.id,
								name: category.title,
							}))}
							placeholder="Select Category"
							id="category"
							value={selectedCategory || null}
							style={{ width: '100%' }}
							disabled={categoryStatus === 'loading'}
							onSelectChange={categoryChangeHandler}
							variant=""
							background="white"
						/>
					</FilterItem>
				</FilterContainer>
				<SortingOptionContainer>
					<ActionButton
						variant="contained"
						color="info"
						onClick={handleShowConfirmationDialog}
						size="large"
						disableElevation
						startIcon={<SortIcon size="large" />}
					>
						Sort by Top Selling
					</ActionButton>
				</SortingOptionContainer>
			</OptionContainer>
			{countryStatus === 'loading' ||
				isLoading ||
				(categoryStatus === 'loading' && (
					<LoaderContainer>
						<CircularProgress />
					</LoaderContainer>
				))}
			{countryStatus !== 'loading' && categoryStatus !== 'loading' && !isLoading && (
				<>
					<DraggableContainer>
						<DragDropContext onDragEnd={dragEndHandler}>
							{productList.map((column, index) => (
								<ProductContainer {...column} index={index} />
							))}
						</DragDropContext>
					</DraggableContainer>
					<ActionContainer>
						<ActionButton
							variant="outlined"
							color="error"
							onClick={resetProducts}
							size="large"
							disableElevation
							startIcon={<RestartAltIcon size="large" />}
						>
							Reset
						</ActionButton>
						<ActionButton
							variant="contained"
							color="primary"
							onClick={submitSortHandler}
							size="large"
							disableElevation
							startIcon={<SaveIcon size="large" />}
						>
							Save
						</ActionButton>
					</ActionContainer>
				</>
			)}
			<ConfirmationDialog
				openDialog={{ show: showConfirmationDialog }}
				title={confirmationDialogData.title}
				message={confirmationDialogData.message}
				yesHandler={sortProductsByTrending}
				onClose={closeConfirmationDialog}
			/>
		</ApplicationPageTemplate>
	);
};

export default SortSku;
